import { z } from 'zod';

type LanguagePriority = 'user' | 'application';

const FeatureFlagsSchema = z.object({
  enableAccountDeletion: z.boolean({
    description: 'Enable personal account deletion.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION',
  }),
  enableTeamDeletion: z.boolean({
    description: 'Enable team deletion.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION',
  }),
  enableTeamAccounts: z.boolean({
    description: 'Enable team accounts.',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS',
  }),
  enableTeamCreation: z.boolean({
    description: 'Enable team creation.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION',
  }),
  enablePersonalAccountBilling: z.boolean({
    description: 'Enable personal account billing.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING',
  }),
  enableTeamAccountBilling: z.boolean({
    description: 'Enable team account billing.',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING',
  }),
  languagePriority: z
    .enum(['user', 'application'], {
      required_error: 'Provide the variable NEXT_PUBLIC_LANGUAGE_PRIORITY',
      description: `If set to user, use the user's preferred language. If set to application, use the application's default language.`,
    })
    .default('application'),
  enableNotifications: z.boolean({
    description: 'Enable notifications functionality',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_NOTIFICATIONS',
  }),
  /*
   * 🔴 `realtimeNotifications` ist am 08.09.2026 ERSATZLOS gefallen — Befund
   * 424: „ein Schalter steht auf «an» und täuscht Funktion vor … ein Schalter,
   * der nichts schaltet, ist eine Falle für den Nächsten, der ihn liest."
   *
   * Er hing an einem Haken, der seit dem Umbau auf den Live-Kontext der Route
   * nichts mehr tat. Die Glocke lebt seitdem über
   * `LiveKontextProvider` (`app/home/[account]/_lib/contexts/live-kontext.tsx`,
   * Tabelle `notifications`) und hängt an keinem Schalter. Wer hier wieder
   * einen einführt, führt ihn nur mit einer Wirkung ein.
   */
  enableVersionUpdater: z.boolean({
    description: 'Enable version updater',
    required_error: 'Provide the variable NEXT_PUBLIC_ENABLE_VERSION_UPDATER',
  }),
  enableTeamAccountsOnly: z.boolean({
    description: 'Enable team accounts only mode (disable personal accounts)',
    required_error:
      'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_ONLY',
  }),
  enablePremiumChannels: z.boolean({
    description:
      'Enable premium channel gating (WhatsApp requires active subscription)',
  }),
  enablePropertyManagement: z.boolean({
    description: 'Enable property management (Bestandsverwaltung) module.',
  }),
  enablePostHog: z.boolean({
    description: 'Enable PostHog product analytics.',
  }),
});

const featuresFlagConfig = FeatureFlagsSchema.parse({
  enableAccountDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION,
    false,
  ),
  enableTeamDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION,
    false,
  ),
  enableTeamAccounts: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS,
    true,
  ),
  enableTeamCreation: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION,
    true,
  ),
  enablePersonalAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING,
    false,
  ),
  enableTeamAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING,
    false,
  ),
  languagePriority: process.env
    .NEXT_PUBLIC_LANGUAGE_PRIORITY as LanguagePriority,
  enableNotifications: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_NOTIFICATIONS,
    true,
  ),
  enableVersionUpdater: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_VERSION_UPDATER,
    false,
  ),
  enableTeamAccountsOnly: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_ONLY,
    false,
  ),
  enablePremiumChannels: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PREMIUM_CHANNELS,
    true,
  ),
  /*
   * 🔴 Vorgabe `false`, nicht `true` — gedreht am 28.08.2026 (#1078).
   *
   * Die Variable steht in `apps/web/.env` auf `false` und in allen vier
   * Vercel-Umgebungen; der Vorgabewert greift also heute NIE. Genau deshalb
   * war er gefaehrlich: er wirkte nur in dem einen Fall, in dem jemand die
   * Zeile verliert — und lieferte dann den EIN-Zweig eines Moduls aus, das
   * nirgends eingeschaltet ist und dessen Datenmodell nicht steht.
   *
   * Ein Vorgabewert, der dem Zustand JEDER Umgebung widerspricht, ist keine
   * Vorgabe, sondern eine Falle mit Zuendschnur. Die Vorgabe ist jetzt das,
   * was ueberall tatsaechlich gilt.
   */
  enablePropertyManagement: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PROPERTY_MANAGEMENT,
    false,
  ),
  enablePostHog: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_POSTHOG_ANALYTICS,
    true,
  ),
} satisfies z.infer<typeof FeatureFlagsSchema>);

export default featuresFlagConfig;

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}
